import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Swal from 'sweetalert2';

ForgotPassword.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

function ForgotPassword({ open, handleClose }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const handleDialogClose = () => {
    setEmail('');
    setErrorMessage('');
    handleClose();
  };

  const verifyEmail = async (emailAddress) => {
    try {
      setLoading(true);
      const response = await fetch('https://dprincess-training.servehttp.com/validate-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailAddress }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Email verification successful:', data);        

        // Limpiar el formulario
        setEmail('');
        setErrorMessage('');

        // Guardar el token de verificación en el almacenamiento local
        localStorage.setItem('verificationCodeToken', data.token);
        localStorage.setItem('verificationCode', data.verificationCode);

        // Cerrar el modal
        handleDialogClose();

        Swal.fire({
          icon: 'success',
          title: '¡Código de verificación enviado!',
          html: `Se ha enviado un código para restablecer su contraseña a: <strong>${emailAddress}</strong>
          <br><br>Por favor, revise su bandeja de entrada. Si no recibe el correo electrónico, verifique su carpeta de spam o de correos no deseados.
          <br><br>En breve será redirigido a la página de verificación del código de seguridad.
          `,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#4a148c',
          timer: 15000,
          timerProgressBar: true,
          showClass: {
            popup: 'animate__animated animate__fadeInDown',
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp',
          },
          backdrop: `
            rgba(74, 20, 140, 0.1)
            left top
            no-repeat
          `,
          willClose: () => {
            // Redirigir al usuario a la página de verificación del código
            navigate('/reset-password/check-verification-code');
          },
        });
      } else {
        setErrorMessage(data.message || 'Error al enviar el correo electrónico');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Error de conexión. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!email || email.trim() === '') {
      setErrorMessage('Por favor, introduzca una dirección de correo electrónico.');
      return;
    }

    // Regex to validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Por favor, introduzca una dirección de correo electrónico válida.');
      return;
    }

    setErrorMessage('');
    // Call the function to verify the email
    verifyEmail(email);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{
        component: 'form',
        onSubmit: (event) => {
          event.preventDefault();
          handleDialogClose();
        },
        sx: { backgroundImage: 'none' },
      }}
      sx={{ backdropFilter: 'blur(1px)' }}
      maxWidth="xs"
    >
      <DialogTitle
        sx={{ fontWeight: 'bold', mb: 1, fontSize: 'h5.fontSize', color: '#4a148c' }}
      >
        Restablecer la contraseña
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
      >
        <DialogContentText>
          Introduzca la dirección de correo electrónico de su cuenta y le enviaremos un enlace para
          restablecer su contraseña.
        </DialogContentText>
        <TextField
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          required
          margin="dense"
          id="email"
          name="email"
          label="Dirección de correo electrónico"
          placeholder="Dirección de correo electrónico"
          type="email"
          fullWidth
          variant="outlined"
        />
      </DialogContent>
      
      {/* Área para mostrar loading o errores */}
      <Box sx={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, pb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} sx={{ color: '#4a148c' }} />
            <DialogContentText sx={{ color: '#4a148c', fontWeight: 'medium' }}>
              Enviando código de verificación...
            </DialogContentText>
          </Box>
        ) : errorMessage ? (
          <DialogContentText sx={{ color: 'red', textAlign: 'center' }}>
            {errorMessage}
          </DialogContentText>
        ) : null}
      </Box>
      
      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button
          variant='outlined'
          onClick={handleDialogClose}
          sx={{ mr: 1, color: 'red', borderColor: 'red' }}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#4a148c' }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Enviando...' : 'Continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ForgotPassword;